import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MessageService, SelectItem } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { AuthorizationIcd } from '@core/model';
import { AuthenticationService, LookupService } from '@core/service';
import { ICD_ORDER_TYPE_LIST, PRIMARY_ICD_MESSAGE } from '@core/constants/ICD';
import { ICDCode, ICDOrderType } from '@shared/model';
import { ListboxClickEvent } from 'primeng/listbox';

@Component({
    selector: 'app-auth-request-diagnosis',
    templateUrl: './auth-request-diagnosis.component.html',
    styleUrls: ['./auth-request-diagnosis.component.scss'],
    standalone: false
})
export class AuthRequestDiagnosisComponent implements OnInit {
  @Output() isStepFormValid = new EventEmitter<boolean>();

  @Output() icdChanged = new EventEmitter<ICDCode[]>();

  @Input() authorizationIcdDTOs: AuthorizationIcd[];

  searchForm: FormGroup;
  searchResults: ICDCode[] = [];
  availableICDs: SelectItem[] = [];
  selectedICDs: SelectItem<ICDCode>[] = [];
  primaryICDWarningMessage: string = null;
  isLoading = false;
  isPrimaryPDGM = true;
  isUnknownICD = false;
  readonly orderTypeList: ICDOrderType[] = ICD_ORDER_TYPE_LIST;

  private readonly UNKNOWN_ICD_CODE = 'Unknown';
  private tenantId: string;
  private isExternalUser: boolean;

  get canAddICDs(): boolean {
    const ICD_MAX_COUNT = 4;

    if (this.isUnknownICD) {
      return false;
    }

    return this.selectedICDs.length < ICD_MAX_COUNT;
  }

  constructor(
    private authenticationService: AuthenticationService,
    private lookupService: LookupService,
    private messageService: MessageService,
    private route: ActivatedRoute
  ) {
    this.tenantId = this.route.snapshot.params.tenantId;
    this.isExternalUser = this.authenticationService.isExternalUser;
  }

  ngOnInit() {
    // Get Global Values
    this.primaryICDWarningMessage = this.isExternalUser
      ? PRIMARY_ICD_MESSAGE.EXTERNAL
      : PRIMARY_ICD_MESSAGE.INTERNAL;

    // Build the Form
    this.searchForm = new FormGroup({
      icdSearch: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(10)
      ])
    });

    // Set default values
    this.setDefaultValues();
    // Initial Form Validation Emit
    this.setFormValidity();

    // Check for Unknown ICD
    this.isUnknownICD = this.selectedICDs.some(({ value }) =>
      this.checkICDUnknown(value)
    );
  }

  search(): void {
    const searchText = this.searchForm.get('icdSearch').value;

    this.isLoading = true;
    this.availableICDs = [];
    this.searchResults = [];
    this.lookupService
      .getIcdCodes(this.tenantId, searchText)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(res => {
        if (res?.length > 0) {
          this.searchResults = res
            .filter(
              ({ icdCodeID }) =>
                !this.selectedICDs.find(
                  ({ value: selectedICD }) => icdCodeID === selectedICD.icdCodeID
                )
            )
            .filter(icd =>
              this.isExternalUser
                ? icd.code.toLocaleLowerCase() !== this.UNKNOWN_ICD_CODE.toLowerCase()
                : icd
            )
            .sort((a, b) => a.code.localeCompare(b.code));

          this.availableICDs = this.searchResults.map(icd => this.createICDSelectItem(icd));
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'No ICD Codes Found',
            detail: 'No ICD Codes were found matching that text.'
          });
        }
      });
  }

  addICD({ value: icd, option }: ListboxClickEvent): void {
    this.isUnknownICD = this.checkICDUnknown(option.value);
    this.selectedICDs = this.isUnknownICD ? [option] : [...this.selectedICDs, option];
    this.availableICDs.splice(
      this.availableICDs.findIndex(e => e.value === option.value),
      1
    );

    this.handleAuthorizationIcdChanges();
    this.setFormValidity();
  }

  remove(event: SelectItem<ICDCode>): void {
    if (this.searchResults) {
      const belongsToSearch = this.searchResults.some(r => r.icdCodeID === event.value.icdCodeID);

      // return option back to list
      if (belongsToSearch) {
        this.availableICDs = [...this.availableICDs, event].sort((a, b) =>
          a.label.localeCompare(b.label)
        );
      }
    }

    const isUnknownICD = this.checkICDUnknown(event.value);

    // we should remove unknown ICD flag on unknown item removal
    if (isUnknownICD) {
      this.isUnknownICD = false;
    }

    this.selectedICDs.splice(
      this.selectedICDs.findIndex(e => e.value === event.value),
      1
    );
    this.handleAuthorizationIcdChanges();
    this.setFormValidity();
  }

  moveUp(value: ICDCode): void {
    const selectedIndex = this.selectedICDs.findIndex(e => e.value === value);
    const temp = this.selectedICDs[selectedIndex];
    this.selectedICDs[selectedIndex] = this.selectedICDs[selectedIndex - 1];
    this.selectedICDs[selectedIndex - 1] = temp;
    this.handleAuthorizationIcdChanges();
  }

  moveDown(value: ICDCode): void {
    const selectedIndex = this.selectedICDs.findIndex(e => e.value === value);
    const temp = this.selectedICDs[selectedIndex];
    this.selectedICDs[selectedIndex] = this.selectedICDs[selectedIndex + 1];
    this.selectedICDs[selectedIndex + 1] = temp;
    this.handleAuthorizationIcdChanges();
  }

  private setDefaultValues(): void {
    if (this.authorizationIcdDTOs && this.authorizationIcdDTOs.length > 0) {
      this.selectedICDs = this.sortICDsByOrderType(this.authorizationIcdDTOs);
      this.checkPrimaryPDGM();
    }
  }

  private setFormValidity(): void {
    const isValid = this.selectedICDs && this.selectedICDs.length > 0;
    this.isStepFormValid.emit(isValid);
  }

  private handleAuthorizationIcdChanges(): void {
    this.icdChanged.emit(this.selectedICDs.map(item => item.value));
    this.checkPrimaryPDGM();
  }

  private checkPrimaryPDGM(): void {
    if (this.selectedICDs.length === 0) {
      this.isPrimaryPDGM = true;
    } else {
      const [primaryICD] = this.selectedICDs;
      this.isPrimaryPDGM = primaryICD.value.pdgm;
    }
  }

  private sortICDsByOrderType(codes: AuthorizationIcd[]): SelectItem<ICDCode>[] {
    return this.orderTypeList.reduce((acc, type) => {
      const code = codes.find(f => f.icdorderType === type);

      if (code) {
        acc.push(this.createICDSelectItem(code.icdCodeDTO));
      }

      return acc;
    }, []);
  }

  private createICDSelectItem(icd: ICDCode): SelectItem<ICDCode> {
    return {
      value: icd,
      label: `${icd.code}${icd.pdgm ? ' (PDGM)' : ''} - ${icd.description}`
    };
  }

  private checkICDUnknown(icd: ICDCode): boolean {
    return icd.code === this.UNKNOWN_ICD_CODE;
  }
}
